import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { COMMON_MOBILE_BREAKPOINT } from '@elfsight/lib-utils';
import { defaultRect, useStableRect } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import type { Rect, SetElement } from 'react-use-rect';
import {
  PADDING_BASE,
  SMALL_MOBILE_BREAKPOINT,
  TWO_COLUMN_BREAKPOINT
} from './layout/const';

type WidgetDimensionsContextValue = {
  isMobile: boolean;
  isSmallMobile: boolean;
  rect: Rect;
  insidePopup: boolean;
  padding: [number, number];
  layout:
    | 'default'
    | 'oneColumnInsideBusinessCard'
    | 'twoColumnsWithAsideBusinessCard';
  ref: SetElement | null;
};

const WidgetDimensionsContext = createContext<WidgetDimensionsContextValue>({
  isMobile: false,
  isSmallMobile: false,
  rect: defaultRect,
  insidePopup: false,
  padding: [PADDING_BASE, PADDING_BASE],
  layout: 'default',
  ref: null
});

export type WidgetDimensionsProviderProps = PropsWithChildren;

const multiplyPaddings = (
  multiplier: number | number[] = 1
): [number, number] => {
  const yPadding = Array.isArray(multiplier) ? multiplier[0] : multiplier;
  const xPadding = Array.isArray(multiplier) ? multiplier[1] : multiplier;
  return [PADDING_BASE * yPadding, PADDING_BASE * xPadding];
};

export function WidgetDimensionsProvider({
  children
}: WidgetDimensionsProviderProps) {
  const { displayBusinessCard, embedType, renderThumbnailMode } = useSettings();
  const [rect, setRect] = useState<Rect>(defaultRect);
  const [ref] = useStableRect(rect, setRect);
  const [isMobile, setIsMobile] = useState(false);
  const [isSmallMobile, setIsSmallMobile] = useState(false);
  const [padding, setPadding] = useState<[number, number]>([
    PADDING_BASE,
    PADDING_BASE
  ]);
  const [layout, setLayout] = useState<
    | 'default'
    | 'oneColumnInsideBusinessCard'
    | 'twoColumnsWithAsideBusinessCard'
  >('default');

  const insidePopup = embedType !== 'inline-form';

  useEffect(() => {
    setLayout(() => {
      if (!displayBusinessCard || !rect.width) {
        return 'default';
      }

      const canUseTwoColumn =
        rect.width >= TWO_COLUMN_BREAKPOINT - (renderThumbnailMode ? 120 : 0);

      return canUseTwoColumn
        ? 'twoColumnsWithAsideBusinessCard'
        : 'oneColumnInsideBusinessCard';
    });
  }, [rect.width, displayBusinessCard, renderThumbnailMode]);

  useEffect(() => {
    setPadding(() => {
      const shouldUseThinPadding = rect.width < COMMON_MOBILE_BREAKPOINT;
      const canUseWidePadding = rect.width > TWO_COLUMN_BREAKPOINT;

      if (shouldUseThinPadding) {
        return multiplyPaddings([0.5, 0.5]);
      }

      if (insidePopup && canUseWidePadding) {
        return multiplyPaddings(1.5);
      }

      return multiplyPaddings(1);
    });
  }, [rect.width, insidePopup]);

  useEffect(() => {
    setIsMobile(rect.width - padding[1] * 2 < COMMON_MOBILE_BREAKPOINT);
    setIsSmallMobile(rect.width - padding[1] * 2 < SMALL_MOBILE_BREAKPOINT);
  }, [rect.width, padding]);

  const value = useMemo<WidgetDimensionsContextValue>(
    () => ({
      isMobile,
      isSmallMobile,
      rect: rect || defaultRect,
      insidePopup,
      padding,
      layout,
      ref
    }),
    [isMobile, isSmallMobile, rect, insidePopup, padding, layout, ref]
  );

  return (
    <WidgetDimensionsContext.Provider value={value}>
      {children}
    </WidgetDimensionsContext.Provider>
  );
}

export const useWidgetDimensions = () => {
  const context = useContext(WidgetDimensionsContext);

  if (context === null) {
    throw new Error(
      `useWidgetDimensionsContext should be used within WidgetDimensionsContext`
    );
  }

  return context;
};
